import { Link } from "react-router-dom";

import { Chip, chipClass } from "@/components/badge/chip";
import { UserAvatar, UserChip } from "@/components/user-chip";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { mutateRequest, optimisticEngagements, putFetcher } from "@/lib/swr";
import useSWRMutation from "swr/mutation";
import type { Engagement } from "@/types/domain";
import { PRIORITIES, priorityOf } from "@/constants/priorities";
import { PriorityChip } from "@/components/engagements/priority-chip";
import type { User } from "@/lib/swr";
import { useUser, useUsersByIds } from "@/hooks/users";

// The PRD page (/e/:id) reads the engagement off `/prds/:engagementId`;
// version lists update optimistically.
const useEngagementUpdate = (engagement: Engagement) =>
  useSWRMutation(`/prds/${engagement.id}`, putFetcher, {
    onSuccess: () => mutateRequest(`/prds/${engagement.id}`),
  });

// Priority P0–P3 dropdown (optimistic update on change); plain chip when readOnly.
export const EngagementPriority = ({
  engagement,
  readOnly,
}: {
  engagement: Engagement;
  readOnly?: boolean;
}) => {
  const { trigger } = useEngagementUpdate(engagement);

  const handleUpdate = (value: number) => {
    const data = { priority: value };
    optimisticEngagements(engagement.id, data);
    trigger(data);
  };
  // Same box as the editable xs button (and the short-ID chip beside it).
  const current = priorityOf(engagement);
  if (readOnly) return <PriorityChip engagement={engagement} className="inline-flex h-6 items-center" />;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="xs"
          title="优先级"
          className={chipClass(current.tone, "font-normal tabular-nums")}
        >
          {current.label}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[200px]">
        {PRIORITIES.map(
          ({ value, label, tone }) =>
            current.value !== value && (
              <DropdownMenuItem key={value} onClick={() => handleUpdate(value)}>
                <Chip tone={tone} className="tabular-nums">{label}</Chip>
              </DropdownMenuItem>
            ),
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// Members (the people building/testing this PRD in the version), grouped by
// their global functional role — developers first, then testers, then the
// rest. The row is scan-level: avatars only (same size as the owner's), with
// the full "Dev: … · QA: …" breakdown in the tooltip; editing lives in the
// detail sheet.
export const EngagementMembers = ({ engagement }: { engagement: Engagement }) => {
  const members = useUsersByIds(engagement.memberIds);
  if (members.length === 0) {
    return (
      <span className="text-xs text-muted-foreground/40" title="No members">
        —
      </span>
    );
  }
  const hasRole = (u: User, role: string) => !!u.roles?.includes(role);
  const devs = members.filter((m) => hasRole(m, "developer"));
  const testers = members.filter(
    (m) => hasRole(m, "tester") && !hasRole(m, "developer"),
  );
  const rest = members.filter((m) => !devs.includes(m) && !testers.includes(m));
  const names = (list: User[]) => list.map((m) => m.name ?? m.id).join(", ");
  const tooltip = [
    devs.length ? `Dev: ${names(devs)}` : null,
    testers.length ? `QA: ${names(testers)}` : null,
    rest.length ? `Other: ${names(rest)}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  const groups = [devs, testers, rest].filter((g) => g.length > 0);
  return (
    <span className="flex items-center gap-1.5" title={tooltip}>
      {groups.map((group, i) => (
        <span key={i} className="flex items-center -space-x-1.5">
          {group.map((m) => (
            <UserAvatar
              key={m.id}
              user={m}
              className="border border-background"
            />
          ))}
        </span>
      ))}
    </span>
  );
};

// PRD owner, resolved from the user directory: engagement.prd carries ownerId only.
export const EngagementOwner = ({ engagement }: { engagement: Engagement }) => {
  const ownerId = engagement?.prd?.ownerId;
  const owner = useUser(ownerId);
  if (!owner) return <span className="text-muted-foreground">-</span>;
  return (
    <Link
      target="_blank"
      to={`/prds?ownerId=${ownerId}`}
      title={owner.name}
      className="flex min-w-0 hover:text-primary"
      onClick={(e) => e.stopPropagation()}
    >
      <UserChip user={owner} />
    </Link>
  );
};
